import { BulkEditPanelData } from 'features/drilldown/panels/global/bulkEdit/BulkEditPanel'
import { DrilldownRolodexType } from 'features/drilldown/rolodex/ontology/ontologyTypes'
import { useNestedSetter } from 'features/drilldown/utils/useNestedSetter'
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'

export const useBulkEditState = (
  panelData: BulkEditPanelData,
  setPanelData: Dispatch<SetStateAction<BulkEditPanelData>>
) => {
  // Local state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editedValues, setEditedValues] = useState<Record<string, unknown>>({})

  // Individual field setters using useNestedSetter
  const setSelectedField = useNestedSetter(setPanelData, 'selectedField')
  const setCustomPropertyName = useNestedSetter(
    setPanelData,
    'customPropertyName'
  )
  const setCustomPropertyType = useNestedSetter(
    setPanelData,
    'customPropertyType'
  )
  const setPrefillValue = useNestedSetter(setPanelData, 'prefillValue')
  const setIsSelectedFieldCustomProperty = useNestedSetter(
    setPanelData,
    'isEditingCustomProperty'
  )
  const setShouldPrefillValues = useNestedSetter(
    setPanelData,
    'shouldPrefillValues'
  )

  // Custom setter for selected type that also clears the field
  const setSelectedType = useCallback(
    (value: DrilldownRolodexType) => {
      setPanelData((prev) => ({
        ...prev,
        selectedType: value,
        selectedField: '', // Clear the field when type changes
        customPropertyName: '', // Also clear custom property name
        isEditingCustomProperty: false, // Reset to standard field mode
      }))
      setEditedValues({}) // Clear any edited values
      setPrefillValue('') // Clear prefill value
    },
    [setPanelData, setEditedValues, setPrefillValue]
  )

  // Computed values
  const propertyKey = panelData.isEditingCustomProperty
    ? panelData.customPropertyName
    : panelData.selectedField.split('.')[1] || null

  // Actions
  const handlePropertySelectionChange = useCallback(
    (value: string) => {
      setPrefillValue('')
      setEditedValues({})
      if (value === 'custom') {
        setIsSelectedFieldCustomProperty(true)
        setSelectedField('')
      } else {
        setIsSelectedFieldCustomProperty(false)
        setSelectedField(value)
      }
    },
    [
      setPrefillValue,
      setEditedValues,
      setIsSelectedFieldCustomProperty,
      setSelectedField,
    ]
  )

  const handleValueChange = useCallback((id: string, newValue: unknown) => {
    setEditedValues((prev) => ({
      ...prev,
      [id]: newValue,
    }))
  }, [])

  const resetState = useCallback(() => {
    setEditedValues({})
    setPrefillValue('')
    setShouldPrefillValues(false)
  }, [setPrefillValue, setShouldPrefillValues])

  return useMemo(
    () => ({
      // Panel data
      panelData,
      setPanelData,

      // Local state
      isSubmitting,
      setIsSubmitting,
      isModalVisible,
      setIsModalVisible,
      editedValues,
      setEditedValues,

      // Computed values
      propertyKey,

      // Setters
      setSelectedType,
      setSelectedField,
      setCustomPropertyName,
      setCustomPropertyType,
      setPrefillValue,
      setIsSelectedFieldCustomProperty,
      setShouldPrefillValues,

      // Actions
      handlePropertySelectionChange,
      handleValueChange,
      resetState,
    }),
    [
      panelData,
      setPanelData,
      isSubmitting,
      setIsSubmitting,
      isModalVisible,
      setIsModalVisible,
      editedValues,
      setEditedValues,
      propertyKey,
      setSelectedType,
      setSelectedField,
      setCustomPropertyName,
      setCustomPropertyType,
      setPrefillValue,
      setIsSelectedFieldCustomProperty,
      setShouldPrefillValues,
      handlePropertySelectionChange,
      handleValueChange,
      resetState,
    ]
  )
}
